import React, { useContext, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Button } from "rsuite";
import styled, { css } from "styled-components";

import { logoutUser, rulesAccept, privacyAccept } from "actions/auth";
import { withTranslation } from "hoc/WithTranslation";
import { APP_TYPE_CLIENT } from "const";
import { useWindowWidth } from "hooks";
import { CustomModal } from "components/base";
import { ButtonPrimary } from "components/base/BaseButton";

import { RtlContext } from "App";
import m from "definedMessages";


const RulesRegulationWidget = ({
    lang,
    rulesLinkEnglish,
    rulesLinkArabic,
    rulesTextEnglish,
    rulesTextArabic,

    privacyLinkEnglish,
    privacyLinkArabic,
    privacyTextEngish,
    privacyTextArabic,

    privacyNotAccepted,
    rulesNotAccepted,
    privacyAccept,
    rulesAccept,
    authInfo,
    logoutUser,
    appType,
    formatMessage
}) => {

    const rtl = useContext(RtlContext);

    const windowWidth = useWindowWidth();
    const [showRulesModal, setShowRulesModal] = useState(null);
    const [showPrivacyModal, setShowPrivacyModal] = useState(null);

    const isTest = authInfo?.session?.is_test;

    if (appType !== APP_TYPE_CLIENT || isTest) {
        return null;
    }

    const [rulesFirstLine, ...rulesText] = (rtl ? rulesTextArabic : rulesTextEnglish).split("\n");
    const isMobile = windowWidth < 720;
    const showRules = showRulesModal || (showRulesModal === null && (rulesNotAccepted === null || rulesNotAccepted));
    const showPrivacy = showPrivacyModal || (showPrivacyModal === null && (privacyNotAccepted === null || privacyNotAccepted));
    const policyText = rtl ? privacyTextArabic : privacyTextEngish;

    return (
        <>
            <FixedLinks>
                <LinkButton
                    rtl={rtl}
                    appearance="link"
                    onClick={setShowRulesModal}
                >
                    {rtl ? rulesLinkArabic : rulesLinkEnglish}
                </LinkButton>
                <LinkButton
                    rtl={rtl}
                    appearance="link"
                    onClick={setShowPrivacyModal}
                >
                    {rtl ? privacyLinkArabic : privacyLinkEnglish}
                </LinkButton>
            </FixedLinks>
            {showRules && <CustomModal
                show={showRules}
                showFooter={rulesNotAccepted === null || rulesNotAccepted}
                confirmButtonText={formatMessage(m.accept)}
                title={rtl ? rulesLinkArabic : rulesLinkEnglish}
                onConfirm={() => {
                    rulesAccept(lang);
                    setShowRulesModal(false);
                }}
                onClose={() => {
                    if (rulesNotAccepted === null || rulesNotAccepted) {
                        logoutUser();
                    }
                    setShowRulesModal(false);
                }}
                width={isMobile ? windowWidth : 720}
            >
                {rulesNotAccepted ? <StyledHeader><b>{formatMessage(m.rulesAndRegulationsWereUpdated)}</b></StyledHeader> : <></>}
                <StyledText>
                    {rulesFirstLine}
                    {rulesText.map(line => {
                        return <><br />{line}</>
                    })}
                </StyledText >
                <ExtraFooter>
                    {rulesNotAccepted === false && isMobile ? <StyledCancelButton
                        onClick={() => {
                            setShowRulesModal(false);
                        }}
                    >
                        {formatMessage(m.cancel)}
                    </StyledCancelButton> : <></>}
                </ExtraFooter>
            </CustomModal>}
            {showPrivacy && <ScrollablePrivacyModal
                show={showPrivacy}
                showFooter
                title={rtl ? privacyLinkArabic : privacyLinkEnglish}
                customFooter={() => (
                    <ButtonPrimary
                        minWidth={100}
                        onClick={() => {
                            if (privacyNotAccepted === null || privacyNotAccepted) {
                                privacyAccept(lang);
                            }
                            setShowPrivacyModal(false);
                        }}
                    >
                        {privacyNotAccepted === null || privacyNotAccepted ? formatMessage(m.accept) : "Ok"}
                    </ButtonPrimary>
                )}
                onClose={() => setShowPrivacyModal(false)}
                width={820}
            >
                {privacyNotAccepted ? <StyledHeader><b>{formatMessage(m.privacyWasUpdated)}</b></StyledHeader> : <></>}
                <StyledText rtl={rtl}>
                    <div className="Container" dangerouslySetInnerHTML={{ __html: policyText }} />
                </StyledText>
            </ScrollablePrivacyModal>}
        </>
    );
}

const ScrollablePrivacyModal = ({ children, customFooter, ...props }) => {
    const endRef = useRef(null);
    const [entered, setEntered] = useState(false);
    const [hasReachedBottom, setHasReachedBottom] = useState(false);

    useEffect(() => {
        if (!entered || hasReachedBottom) return;

        const end = endRef.current;
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting && entry.intersectionRatio === 1) {
                setHasReachedBottom(true);
            }
        }, {
            root: end.closest(".rs-modal-body"),
            threshold: 1
        });

        observer.observe(end);
        return () => observer.disconnect();
    }, [entered, hasReachedBottom]);

    return (
        <PrivacyModal
            {...props}
            onEntered={() => setEntered(true)}
            customFooter={() => (
                <div style={{ visibility: hasReachedBottom ? "visible" : "hidden" }}>
                    {customFooter()}
                </div>
            )}
        >
            {children}
            <div ref={endRef} style={{ height: 1 }} aria-hidden="true" />
        </PrivacyModal>
    );
};

const PrivacyModal = styled(CustomModal)`
    &&&& {
        .rs-modal-header {
            justify-content: space-between;
        }

        .rs-modal-footer {
            padding-left: 20px;
            padding-right: 20px;
        }

        @media (max-width: 768px) {
            margin: 0 !important;
            width: 100% !important;
            height: 100% !important;

            .rs-modal-dialog {
                height: 100%;
            }

            .rs-modal-content {
                display: flex;
                flex-direction: column;
                height: 100%;
                border-radius: 0;
            }

            .rs-modal-header,
            .rs-modal-footer {
                flex-shrink: 0;
            }

            .rs-modal-body {
                flex: 1;
                min-height: 0;
                max-height: none !important;
                overflow-y: auto;
            }
        }
    }
`;

const StyledHeader = styled.div`
    && {
        color: #fd5e59;
    }
`;

const FixedLinks = styled.div`
    && {
        position: fixed;
        bottom: 20px;
        left: 35px;

        display: flex;

        @media (max-width: 767px) {
            bottom: 20px;
            left: 10px;
        }

           
        ${props => props.rtl && css`
            right: 35px;
            left: auto;
    
            @media (max-width: 767px) {
                right: 10px;
                left: auto;
            }
        `}
    }
`;

const LinkButton = styled(Button)`
    && {
        z-index: 200;
    }
`;

const ExtraFooter = styled.div`
    display: flex;
    justify-content: end;
    padding-right: 10px;
`;

const StyledCancelButton = styled(Button)`
    margin-top: 20px;
`;

const StyledText = styled.p`
    text-align: justify;
    padding-left: 10px;
    padding-right: 20px;
    
    .rtl & {
        padding-left: 20px;
        padding-right: 10px;
    }
`;


const mapState = ({ references, auth_dialer, auth }) => ({
    rulesLinkEnglish: references.rules_and_regulations_link_english,
    rulesLinkArabic: references.rules_and_regulations_link_arabic,
    rulesTextEnglish: references.rules_and_regulations_text_english,
    rulesTextArabic: references.rules_and_regulations_text_arabic,
    privacyLinkEnglish: references.privacy_policy_link_english,
    privacyLinkArabic: references.privacy_policy_link_arabic,
    privacyTextEngish: references.privacy_policy_text_english,
    privacyTextArabic: references.privacy_policy_text_arabic,

    rulesNotAccepted: auth.rulesNotAccepted,
    privacyNotAccepted: auth.privacyNotAccepted,
    dialerInfo: auth_dialer.dialerInfo,
    authInfo: auth.authInfo,
    appType: auth.appType,
    lang: auth.lang
});

export default withTranslation(connect(mapState, { rulesAccept, privacyAccept, logoutUser })(RulesRegulationWidget));
